import { useState } from 'react';
import { AdminLayout } from '../components/Layout/AdminLayout';
import { PageHeader, StatCard } from '../components/common';
import {
  Box,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Alert,
  Chip,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import {
  History,
  Person,
  CalendarToday,
  Security,
  Edit,
  Add,
  Delete,
  Visibility,
} from '@mui/icons-material';
import { DataGrid, GridColDef, GridToolbar } from '@mui/x-data-grid';
import { colors, transitions, radius } from '../theme/theme';

// Using query
import { useQuery } from '@tanstack/react-query';
import apiClient from '../api/client';

export const AuditTrailPage = () => {
  const [actionFilter, setActionFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const { data: report, isLoading, error } = useQuery({
    queryKey: ['reports', 'audit-trail', actionFilter, dateFrom, dateTo],
    queryFn: async () => {
      const params: any = {};
      if (actionFilter) params.action = actionFilter;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;

      const response = await apiClient.get('/admin/county/reports/audit-trail', { params });
      return response.data;
    },
  });

  const getActionIcon = (action: string) => {
    const actionLower = action?.toLowerCase() || '';
    if (actionLower.includes('create') || actionLower.includes('add')) return <Add sx={{ fontSize: 16 }} />;
    if (actionLower.includes('update') || actionLower.includes('edit')) return <Edit sx={{ fontSize: 16 }} />;
    if (actionLower.includes('delete') || actionLower.includes('remove')) return <Delete sx={{ fontSize: 16 }} />;
    if (actionLower.includes('view') || actionLower.includes('access')) return <Visibility sx={{ fontSize: 16 }} />;
    return <History sx={{ fontSize: 16 }} />;
  };

  const getActionColor = (action: string) => {
    const actionLower = action?.toLowerCase() || '';
    if (actionLower.includes('create') || actionLower.includes('add')) return 'success';
    if (actionLower.includes('update') || actionLower.includes('edit')) return 'info';
    if (actionLower.includes('delete') || actionLower.includes('remove')) return 'error';
    if (actionLower.includes('view') || actionLower.includes('access')) return 'default';
    return 'warning';
  };

  return (
    <AdminLayout>
      <Box>
        <PageHeader
          title="Audit Trail"
          subtitle="Monitor system activity and user actions"
          breadcrumbs={[
            { label: 'Dashboard', path: '/dashboard' },
            { label: 'Reports' },
            { label: 'Audit Trail' },
          ]}
        />

        {/* Filters */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
              Filters
            </Typography>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <FormControl fullWidth size="small">
                  <InputLabel>Action Type</InputLabel>
                  <Select
                    value={actionFilter}
                    onChange={(e) => setActionFilter(e.target.value)}
                    label="Action Type"
                  >
                    <MenuItem value="">All Actions</MenuItem>
                    <MenuItem value="create">Create</MenuItem>
                    <MenuItem value="update">Update</MenuItem>
                    <MenuItem value="delete">Delete</MenuItem>
                    <MenuItem value="view">View</MenuItem>
                    <MenuItem value="login">Login</MenuItem>
                    <MenuItem value="logout">Logout</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <TextField
                  fullWidth
                  label="Date From"
                  type="date"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  size="small"
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <TextField
                  fullWidth
                  label="Date To"
                  type="date"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  size="small"
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            Failed to load audit trail. Please try again.
          </Alert>
        )}

        {isLoading ? (
          <Box display="flex" justifyContent="center" alignItems="center" py={8}>
            <CircularProgress />
          </Box>
        ) : report ? (
          <>
            {/* Summary Stats */}
            <Grid container spacing={3} mb={3}>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <StatCard
                  title="Total Actions"
                  value={report.totalActions?.toLocaleString() || 0}
                  icon={<History />}
                  color={colors.primary.main}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <StatCard
                  title="Unique Users"
                  value={report.uniqueUsers?.toLocaleString() || 0}
                  icon={<Person />}
                  color={colors.success.main}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <StatCard
                  title="Today's Actions"
                  value={report.todayActions?.toLocaleString() || 0}
                  icon={<CalendarToday />}
                  color={colors.info.main}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <StatCard
                  title="This Week"
                  value={report.thisWeekActions?.toLocaleString() || 0}
                  icon={<Security />}
                  color={colors.warning.main}
                />
              </Grid>
            </Grid>

            {/* Most Active Users */}
            {report.mostActiveUsers && report.mostActiveUsers.length > 0 && (
              <Card sx={{ mb: 3 }}>
                <CardContent>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                    Most Active Users
                  </Typography>
                  <Grid container spacing={2}>
                    {report.mostActiveUsers.map((userStat: any, index: number) => (
                      <Grid key={index} size={{ xs: 12, sm: 6, md: 4 }}>
                        <Box
                          sx={{
                            p: 2,
                            bgcolor: colors.grey[50],
                            borderRadius: 2,
                            border: `1px solid ${colors.grey[200]}`,
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Person sx={{ fontSize: 32, color: colors.primary.main }} />
                            <Box sx={{ flex: 1 }}>
                              <Typography variant="body1" fontWeight={600}>
                                {userStat.user || 'Unknown'}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                {userStat.actions?.toLocaleString() || 0} actions
                              </Typography>
                            </Box>
                          </Box>
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                </CardContent>
              </Card>
            )}

            {/* Audit Trail DataGrid */}
            {report.auditTrails && report.auditTrails.data && report.auditTrails.data.length > 0 && (
              <Card>
                <CardContent>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
                    Activity Log
                  </Typography>
                  <Box sx={{ height: 600, width: '100%' }}>
                    <DataGrid
                      rows={report.auditTrails.data.map((trail: any) => ({
                        id: trail.id,
                        ...trail,
                      }))}
                      columns={[
                        {
                          field: 'created_at',
                          headerName: 'Date & Time',
                          width: 180,
                          renderCell: (params) => (
                            <Typography variant="body2">
                              {new Date(params.value).toLocaleString()}
                            </Typography>
                          ),
                        },
                        {
                          field: 'user',
                          headerName: 'User',
                          width: 180,
                          valueGetter: (value, row) => {
                            if (row.user) {
                              return `${row.user.first_name || ''} ${row.user.last_name || ''}`.trim() || 'Unknown';
                            }
                            return 'System';
                          },
                          renderCell: (params) => (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Person sx={{ fontSize: 18, color: colors.grey[600] }} />
                              <Typography variant="body2" fontWeight={600}>
                                {params.value}
                              </Typography>
                            </Box>
                          ),
                        },
                        {
                          field: 'action',
                          headerName: 'Action',
                          width: 150,
                          renderCell: (params) => (
                            <Chip
                              icon={getActionIcon(params.value)}
                              label={params.value}
                              color={getActionColor(params.value)}
                              size="small"
                              sx={{ textTransform: 'capitalize' }}
                            />
                          ),
                        },
                        {
                          field: 'description',
                          headerName: 'Description',
                          flex: 1,
                          minWidth: 300,
                          renderCell: (params) => (
                            <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                              {params.value || 'No description'}
                            </Typography>
                          ),
                        },
                        {
                          field: 'ip_address',
                          headerName: 'IP Address',
                          width: 140,
                          renderCell: (params) => (
                            <Chip
                              label={params.value || 'N/A'}
                              size="small"
                              variant="outlined"
                              sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}
                            />
                          ),
                        },
                      ] as GridColDef[]}
                      slots={{ toolbar: GridToolbar }}
                      slotProps={{
                        toolbar: {
                          showQuickFilter: true,
                          quickFilterProps: { debounceMs: 500 },
                        },
                      }}
                      initialState={{
                        pagination: { paginationModel: { pageSize: 15 } },
                        sorting: {
                          sortModel: [{ field: 'created_at', sort: 'desc' }],
                        },
                      }}
                      pageSizeOptions={[10, 15, 25, 50, 100]}
                      disableRowSelectionOnClick
                      getRowHeight={() => 'auto'}
                      sx={{
                        '& .MuiDataGrid-cell': {
                          borderBottom: `1px solid ${colors.grey[200]}`,
                          py: 1,
                        },
                        '& .MuiDataGrid-columnHeaders': {
                          bgcolor: colors.grey[100],
                          fontWeight: 700,
                        },
                      }}
                    />
                  </Box>
                </CardContent>
              </Card>
            )}

            {/* No Data Message */}
            {(!report.auditTrails || !report.auditTrails.data || report.auditTrails.data.length === 0) && (
              <Card>
                <CardContent>
                  <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" py={8}>
                    <History sx={{ fontSize: 64, color: colors.grey[400], mb: 2 }} />
                    <Typography variant="h6" color="text.secondary" gutterBottom>
                      No Audit Trail Data
                    </Typography>
                    <Typography variant="body2" color="text.secondary" textAlign="center">
                      There are no audit trail records for the selected filters.
                      <br />
                      Try adjusting your filters.
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            )}
          </>
        ) : (
          <Card>
            <CardContent>
              <Box display="flex" justifyContent="center" alignItems="center" py={8}>
                <Typography color="text.secondary">
                  Loading audit trail data...
                </Typography>
              </Box>
            </CardContent>
          </Card>
        )}
      </Box>
    </AdminLayout>
  );
};
